import Phaser from 'phaser';
import {
  CHEST_ART,
  CHEST_NAMES,
  chestPrizeArt,
  chestPrizeLabel,
  type ChestKind,
  type ChestPrize,
} from '../config/originChests';

/**
 * Opening an Origin chest (spec docs/superpowers/specs/2026-10-13-light-orb-chests-light-shop-design.md):
 * "the chest starts shaking and then after 3 seconds of shaking it explodes
 * and what you get comes out".
 *
 * Pure theatre: the CALLER has already taken the chest off the shelf and paid
 * the prize (`ProgressService.takeChest` + `payChestPrize`), so a tab closed
 * mid-shake keeps what the chest held. `onDone` runs once, from OK.
 */

/**
 * The number both chest rolls take (the drop on a win, the prize on OPEN):
 * the render layer's unseeded random. An e2e build may pin it through
 * `window.__chestRoll`, because pinning `Math.random` itself would also pin
 * every effect that redraws until it differs. Stripped from normal builds.
 */
export function chestRandom(): number {
  if (import.meta.env.VITE_E2E) {
    const forced = (window as unknown as { __chestRoll?: number }).__chestRoll;
    if (typeof forced === 'number') return forced;
  }
  return Math.random();
}

const W = 1280;
const H = 720;
/** Above StorageScene's grid (6) and its item dialogs. */
const DEPTH = 200;
/** The shake, then the burst — the brief's three seconds. */
export const CHEST_SHAKE_MS = 3000;
const CHEST_SIZE = 220;
const CENTRE = { x: W / 2, y: 360 };

interface Palette {
  veil: number;
  burst: [number, number, number];
  title: string;
  stroke: string;
}

const PALETTES: Record<ChestKind, Palette> = {
  // The Dark Chest bursts in deep purple and red-black smoke.
  dark: {
    veil: 0x050308,
    burst: [0x2a0a3a, 0x8a3ad0, 0xff5a8a],
    title: '#d8c8ff',
    stroke: '#14051f',
  },
  // The Light Chest bursts in white and pale gold.
  light: {
    veil: 0x0a1424,
    burst: [0xffffff, 0xfff1b0, 0xffd36b],
    title: '#fff6dc',
    stroke: '#3a2a08',
  },
};

export function openChest(
  scene: Phaser.Scene,
  opts: { kind: ChestKind; prize: ChestPrize; onDone: () => void },
): void {
  const { kind, prize } = opts;
  const pal = PALETTES[kind];
  const objs: Phaser.GameObjects.GameObject[] = [];
  const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
    objs.push(o);
    return o;
  };
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    objs.forEach((o) => {
      scene.tweens.killTweensOf(o);
      o.destroy();
    });
    opts.onDone();
  };

  // A veil that also swallows taps meant for the shelf underneath.
  const veil = add(
    scene.add
      .rectangle(W / 2, H / 2, W, H, pal.veil, 0)
      .setDepth(DEPTH)
      .setInteractive()
      .setName('chest-opening'),
  );
  scene.tweens.add({ targets: veil, fillAlpha: 0.93, duration: 300 });

  add(
    scene.add
      .text(W / 2, 110, CHEST_NAMES[kind], {
        fontSize: '40px',
        fontStyle: 'bold',
        color: pal.title,
        stroke: pal.stroke,
        strokeThickness: 7,
      })
      .setOrigin(0.5)
      .setDepth(DEPTH + 3)
      .setName('chest-opening-title'),
  );

  // A glow behind the chest that swells with the shake.
  const glow = add(
    scene.add.circle(CENTRE.x, CENTRE.y, CHEST_SIZE * 0.62, pal.burst[1], 0.12).setDepth(DEPTH + 1),
  );
  const chest = add(
    scene.add
      .image(CENTRE.x, CENTRE.y, CHEST_ART[kind])
      .setDisplaySize(CHEST_SIZE, CHEST_SIZE)
      .setDepth(DEPTH + 2)
      .setName('chest-opening-chest'),
  );
  const baseScale = chest.scaleX;

  // ── Shake: three seconds, harder and faster as it goes ────────────────────
  const started = scene.time.now;
  const shaker = scene.time.addEvent({
    delay: 16,
    loop: true,
    callback: () => {
      const k = Math.min(1, (scene.time.now - started) / CHEST_SHAKE_MS);
      const amp = 2 + k * 12;
      const speed = 40 - k * 22;
      const t = scene.time.now;
      chest.setPosition(
        CENTRE.x + Math.sin(t / speed) * amp,
        CENTRE.y + Math.cos(t / (speed * 1.3)) * amp * 0.5,
      );
      chest.setAngle(Math.sin(t / (speed * 0.8)) * (3 + k * 9));
      glow.setRadius(CHEST_SIZE * (0.62 + k * 0.3));
      glow.setFillStyle(pal.burst[1], 0.12 + k * 0.3);
    },
  });

  // ── Burst ─────────────────────────────────────────────────────────────────
  scene.time.delayedCall(CHEST_SHAKE_MS, () => {
    if (finished) return;
    shaker.remove();
    scene.cameras.main.shake(320, 0.012);
    scene.tweens.add({
      targets: chest,
      scaleX: baseScale * 1.8,
      scaleY: baseScale * 1.8,
      alpha: 0,
      duration: 260,
      ease: 'Cubic.easeOut',
    });
    scene.tweens.add({ targets: glow, alpha: 0, duration: 260 });
    // Rings of light rushing out from where the chest was.
    pal.burst.forEach((colour, i) => {
      const ring = add(
        scene.add
          .circle(CENTRE.x, CENTRE.y, 20, colour, 0.85 - i * 0.2)
          .setDepth(DEPTH + 1 + i * 0.1),
      );
      scene.tweens.add({
        targets: ring,
        radius: 260 + i * 70,
        alpha: 0,
        duration: 520 + i * 120,
        ease: 'Cubic.easeOut',
      });
    });
    // Sparks thrown outward.
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2;
      const spark = add(
        scene.add.circle(CENTRE.x, CENTRE.y, 6, pal.burst[i % 3], 1).setDepth(DEPTH + 2),
      );
      scene.tweens.add({
        targets: spark,
        x: CENTRE.x + Math.cos(a) * (180 + (i % 3) * 50),
        y: CENTRE.y + Math.sin(a) * (180 + (i % 3) * 50),
        alpha: 0,
        duration: 700,
        ease: 'Quad.easeOut',
      });
    }
    showPrize();
  });

  // ── What came out ─────────────────────────────────────────────────────────
  const showPrize = () => {
    const art = chestPrizeArt(kind, prize);
    // Two shards side by side; one big coin with the amount.
    const xs = prize === 'shards' ? [CENTRE.x - 70, CENTRE.x + 70] : [CENTRE.x];
    xs.forEach((x, i) => {
      if (!scene.textures.exists(art)) return;
      const img = add(
        scene.add
          .image(CENTRE.x, CENTRE.y, art)
          .setDisplaySize(30, 30)
          .setDepth(DEPTH + 4)
          .setName(`chest-prize-${i}`),
      );
      const size = prize === 'shards' ? 130 : 150;
      const s = (img.scaleX * size) / 30;
      scene.tweens.add({
        targets: img,
        x,
        y: CENTRE.y - 30,
        scaleX: s,
        scaleY: s,
        duration: 620,
        delay: 120,
        ease: 'Back.easeOut',
      });
    });
    const label = add(
      scene.add
        .text(W / 2, CENTRE.y + 110, `${chestPrizeLabel(kind, prize)}!`, {
          fontSize: '34px',
          fontStyle: 'bold',
          color: pal.title,
          stroke: pal.stroke,
          strokeThickness: 7,
        })
        .setOrigin(0.5)
        .setDepth(DEPTH + 4)
        .setAlpha(0)
        .setName('chest-prize-label'),
    );
    scene.tweens.add({ targets: label, alpha: 1, duration: 400, delay: 500 });

    const where = prize === 'shards' ? 'They are waiting in STORAGE.' : 'Added to your balance.';
    const sub = add(
      scene.add
        .text(W / 2, CENTRE.y + 152, where, {
          fontSize: '18px',
          color: '#d9d4e6',
          fontStyle: 'italic',
        })
        .setOrigin(0.5)
        .setDepth(DEPTH + 4)
        .setAlpha(0),
    );
    scene.tweens.add({ targets: sub, alpha: 1, duration: 400, delay: 600 });

    const ok = add(
      scene.add
        .text(W / 2, CENTRE.y + 222, 'OK', {
          fontSize: '26px',
          fontStyle: 'bold',
          color: '#ffffff',
          backgroundColor: '#1a6b1a',
          padding: { x: 36, y: 10 },
        })
        .setOrigin(0.5)
        .setDepth(DEPTH + 4)
        .setAlpha(0)
        .setName('chest-opening-ok'),
    );
    scene.tweens.add({
      targets: ok,
      alpha: 1,
      duration: 300,
      delay: 700,
      onComplete: () => {
        ok.setInteractive({ useHandCursor: true });
        ok.on('pointerup', finish);
      },
    });
  };

  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    finished = true;
    shaker.remove();
  });
}
